import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Modal } from "../modal/Modal";
import { Button, makeStyles } from "@fluentui/react-components";
import { ROOM_ENTRY_FAILURE_KIND } from "./room-entry-failure-utils";

const useStyles = makeStyles({
  actions: {
    display: "flex",
    gap: "8px"
  },
  list: {
    paddingLeft: "20px",
    marginBottom: "10px"
  },
  firstItem: {
    marginTop: "10px"
  },
  secondItem: {
    marginTop: "5px"
  }
});

function FailureTitle({ failure }) {
  switch (failure?.kind) {
    case ROOM_ENTRY_FAILURE_KIND.ACCESS_DENIED:
      return <FormattedMessage id="room-entry-error.access-denied-title" defaultMessage="Unable to enter the Verse" />;
    case ROOM_ENTRY_FAILURE_KIND.CAPACITY_FULL:
      return (
        <FormattedMessage id="room-entry-modal.room-full-title" defaultMessage="Active participant limit reached" />
      );
    case ROOM_ENTRY_FAILURE_KIND.NETWORK_ERROR:
      return <FormattedMessage id="room-entry-error.network-title" defaultMessage="Something went wrong" />;
    case ROOM_ENTRY_FAILURE_KIND.RATE_LIMITED:
      return <FormattedMessage id="room-entry-error.rate-limited-title" defaultMessage="Too many entry attempts" />;
    case ROOM_ENTRY_FAILURE_KIND.SERVICE_UNAVAILABLE:
      return <FormattedMessage id="room-entry-error.service-unavailable-title" defaultMessage="Verse unavailable" />;
    default:
      return <FormattedMessage id="ui-root.entering-error" defaultMessage="Unable to Enter the Verse" />;
  }
}

FailureTitle.propTypes = {
  failure: PropTypes.object
};

function FailureMessage({ failure }) {
  switch (failure?.kind) {
    case ROOM_ENTRY_FAILURE_KIND.ACCESS_DENIED:
      return (
        <FormattedMessage
          id="room-entry-error.access-denied-message"
          defaultMessage="You do not have permission to enter this Verse."
        />
      );
    case ROOM_ENTRY_FAILURE_KIND.CAPACITY_FULL:
      return failure.canLookAround ? (
        <FormattedMessage
          id="room-entry-modal.room-full"
          defaultMessage="You can still look around while you wait for a spot."
        />
      ) : (
        <FormattedMessage
          id="room-entry-modal.room-full-no-look-around"
          defaultMessage="Please try again later when an active participant spot opens."
        />
      );
    case ROOM_ENTRY_FAILURE_KIND.NETWORK_ERROR:
      return (
        <FormattedMessage
          id="room-entry-error.network-message"
          defaultMessage="We couldn't open the Verse. Check your connection and try again."
        />
      );
    case ROOM_ENTRY_FAILURE_KIND.RATE_LIMITED:
      return failure.values?.retryAfter ? (
        <FormattedMessage
          id="room-entry-error.rate-limited-message-with-retry-after"
          defaultMessage="Please wait {retryAfter} seconds before trying to enter again."
          values={{ retryAfter: failure.values.retryAfter }}
        />
      ) : (
        <FormattedMessage
          id="room-entry-error.rate-limited-message"
          defaultMessage="Please wait a moment before trying to enter again."
        />
      );
    case ROOM_ENTRY_FAILURE_KIND.SERVICE_UNAVAILABLE:
      return (
        <FormattedMessage
          id="room-entry-error.service-unavailable-message"
          defaultMessage="The Verse is temporarily unavailable. Please try again in a moment."
        />
      );
    default:
      return (
        <FormattedMessage
          id="ui-root.entering-error-message"
          defaultMessage="We couldn't connect you to the Verse. This is often due to browser permission issues. Please follow these steps:"
        />
      );
  }
}

FailureMessage.propTypes = {
  failure: PropTypes.object
};

export const RoomEntryErrorModal = ({ open, failure, onClose, onLookAround, onRetry }) => {
  const classes = useStyles();
  const showLookAround = failure && failure.canLookAround && onLookAround;
  const showRetry = failure && failure.retryable && onRetry;
  const showPermissionSteps = !failure || failure.showPermissionSteps;
  const actions = (
    <div className={classes.actions}>
      <Button appearance={showLookAround || showRetry ? "secondary" : "primary"} onClick={onClose}>
        <FormattedMessage id="room-entry-error.close" defaultMessage="Close" />
      </Button>
      {showLookAround && (
        <Button appearance="primary" onClick={onLookAround}>
          <FormattedMessage id="room-entry-modal.spectate-button" defaultMessage="Look around" />
        </Button>
      )}
      {showRetry && (
        <Button appearance="primary" onClick={onRetry}>
          <FormattedMessage id="ui-root.try-again" defaultMessage="Try Again" />
        </Button>
      )}
    </div>
  );

  return (
    <Modal title={<FailureTitle failure={failure} />} onClose={onClose} open={open} actions={actions}>
      <p>
        <FailureMessage failure={failure} />
      </p>
      {showPermissionSteps && (
        <ol className={classes.list}>
          <li className={classes.firstItem}>
            1.{" "}
            <FormattedMessage
              id="ui-root.entering-error-step-1"
              defaultMessage="Check your browser's permission settings for this website (microphone, camera, motion sensors, xr-spatial-tracking, etc)."
            />
          </li>
          <li className={classes.secondItem}>
            2. <FormattedMessage id="ui-root.entering-error-step-2" defaultMessage="Refresh the page and try again." />
          </li>
        </ol>
      )}
    </Modal>
  );
};

RoomEntryErrorModal.propTypes = {
  open: PropTypes.bool,
  failure: PropTypes.object,
  onClose: PropTypes.func,
  onLookAround: PropTypes.func,
  onRetry: PropTypes.func
};
